import { useState, useCallback, useEffect } from 'react';
import { useNotificationBar } from 'context/notificationContext';
import { UserState, EligibilityRules, CompletionConditions } from 'interfaces/widget-types';
import { useUploadFiles } from 'hooks/upload';
import { useGetItemsParent } from 'hooks/category-manage';
import { getImageSrc, isValidUrl } from 'services/util';
import { Upload } from 'lucide-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ToggleField } from 'components/styledFormElements/ToggleField';
import { SingleSelect } from 'components/styledFormElements/SingleSelect';
import {
    EMPTY_PARENT_PAYLOAD,
    buildRedirectionParentFields,
    normalizeLinkedParentsResponse,
    pickParentPayloadFields,
    shouldResolveLinkedParents,
    toApiEntityType,
} from './linkedParentResolver';

const REDIRECT_TYPES = ['EPISODE', 'SHOW', 'COLLECTION', 'CATEGORY', 'OTHER'] as const;
type BannerRedirectType = (typeof REDIRECT_TYPES)[number];

const normalizeRedirectType = (type?: string): BannerRedirectType => {
    const normalized = (type || 'EPISODE').trim().toUpperCase();
    return (REDIRECT_TYPES as readonly string[]).includes(normalized)
        ? (normalized as BannerRedirectType)
        : 'EPISODE';
};

interface Props {
    widget: {
        value: string;
        label: string;
        code: string;
    };
    initialData?: any;
    onSave: (payload: any) => void;
    onCancel?: () => void;
}

const userStateOptions: { value: UserState; label: string }[] = [
    { value: 'free_user', label: 'Free User' },
    { value: 'paid_user', label: 'Paid User' },
    { value: 'trial_user', label: 'Trial User' },
    { value: 'trial_expired_user', label: 'Trial Expired User' },
];

const completionRuleOptions = [
    { value: 'fixed_date', label: 'Fixed Date/Time' },
    { value: 'conversion_completion', label: 'Conversion Completion' },
    { value: 'manual_disable', label: 'Manual Disable' },
];

const redirectOptions = [
    { value: 'EPISODE', label: 'Episode' },
    { value: 'SHOW', label: 'Show' },
    { value: 'COLLECTION', label: 'Collection' },
    { value: 'CATEGORY', label: 'Category' },
    { value: 'OTHER', label: 'Other' },
];

const MarketingBanner = ({ widget, initialData, onSave, onCancel }: Props) => {
    const notification = useNotificationBar();
    const uploadMutation = useUploadFiles();
    const itemsParentMutation = useGetItemsParent();

    // Extract data from the nested structure
    const widgetData = initialData?.data || initialData;

    console.log('Initial Data:', initialData);
    console.log('Widget Data:', widgetData);
    console.log('Completion Conditions:', widgetData?.completionConditions);

    const [bannerType, setBannerType] = useState<'wide' | 'pill'>(
        widgetData?.bannerType || 'wide'
    );

    // Common fields
    const [bannerName, setBannerName] = useState(widgetData?.bannerName || initialData?.name || '');
    const [enabled, setEnabled] = useState(widgetData?.enabled ?? false);
    const [redirectType, setRedirectType] = useState<BannerRedirectType>(
        normalizeRedirectType(widgetData?.redirection_to)
    );
    const [redirectId, setRedirectId] = useState(
        widgetData?.redirection_id || ''
    );
    const [isWebview, setIsWebview] = useState(Boolean(widgetData?.is_webview));
    const [webToolType, setWebToolType] = useState(widgetData?.web_tool_type || '');
    const [webviewUrl, setWebviewUrl] = useState(widgetData?.webview_url || '');

    // Scheduler
    const [startDate, setStartDate] = useState(widgetData?.scheduler?.startDate || '');
    const [endDate, setEndDate] = useState(widgetData?.scheduler?.endDate || '');

    // Creative Upload
    const [creativeFile, setCreativeFile] = useState<File | null>(null);
    const [creativePreview, setCreativePreview] = useState<string | null>(null);

    // Eligibility Rules
    const [userStates, setUserStates] = useState<UserState[]>(
        widgetData?.eligibilityRules?.userState || []
    );
    const [contentLabels, setContentLabels] = useState<string[]>(
        widgetData?.eligibilityRules?.userBehavior?.hasWatchedContentWithLabels || []
    );
    const [contentLabelsInput, setContentLabelsInput] = useState(contentLabels.join(', '));
    const [freeEpisodes, setFreeEpisodes] = useState(
        widgetData?.eligibilityRules?.userBehavior?.hasCompletedFreeEpisodes || 0
    );

    // Completion Conditions
    const completionConditionsKeys = Object.keys(widgetData?.completionConditions || {});
    console.log('Completion Conditions Keys:', completionConditionsKeys);

    const [completionRules, setCompletionRules] = useState<string[]>(
        completionConditionsKeys.filter(key => {
            const value = widgetData?.completionConditions?.[key];
            console.log(`Checking key ${key}:`, value, typeof value);
            return value === true || (typeof value === 'string' && value.length > 0);
        }).map(key => {
            // Map API key names to checkbox value names
            if (key === 'fixedDateTime') return 'fixed_date';
            if (key === 'conversionCompletion') return 'conversion_completion';
            if (key === 'manualDisable') return 'manual_disable';
            return key;
        }) || []
    );
    console.log('Initial Completion Rules:', completionRules);

    const [fixedDateTime, setFixedDateTime] = useState(
        widgetData?.completionConditions?.fixedDateTime || ''
    );
    console.log('Initial Fixed DateTime:', fixedDateTime);

    // Pill Banner specific
    const [priority, setPriority] = useState(
        widgetData?.priority || 1
    );
    const [dismissible, setDismissible] = useState(
        widgetData?.dismissible ?? false
    );

    // Initialize creative preview from initial data
    useEffect(() => {
        const creativeUrl = widgetData?.thumbnail || initialData?.thumbnail;
        if (creativeUrl && !creativeFile) {
            const imageUrl = typeof creativeUrl === 'string' ? creativeUrl : '';
            const fullImageUrl = imageUrl.startsWith('http')
                ? imageUrl
                : getImageSrc(imageUrl);
            setCreativePreview(fullImageUrl);
        }
    }, [widgetData, initialData, creativeFile]);

    // Cleanup preview
    useEffect(() => {
        return () => {
            if (creativePreview && creativePreview.startsWith('blob:')) {
                URL.revokeObjectURL(creativePreview);
            }
        };
    }, [creativePreview]);

    const handleCreativeFileSelect = useCallback((file: File | null) => {
        if (file) {
            // Validate file
            if (file.size > 5 * 1024 * 1024) {
                notification.push({
                    message: 'File size must be under 5MB',
                    type: 'error',
                });
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                notification.push({
                    message: 'Only JPG, JPEG, PNG, WebP allowed',
                    type: 'error',
                });
                return;
            }

            setCreativeFile(file);
            const previewUrl = URL.createObjectURL(file);
            setCreativePreview(previewUrl);
        } else {
            setCreativeFile(null);
            setCreativePreview(null);
        }
    }, [notification]);

    const handleCreativeFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        handleCreativeFileSelect(file);
    }, [handleCreativeFileSelect]);

    const handleEnabledChange = useCallback((checked: boolean) => {
        setEnabled(checked);
    }, []);

    const handleRedirectTypeChange = useCallback((option: { value: string; label: string }) => {
        setRedirectType(normalizeRedirectType(option.value));
    }, []);

    const handleWebviewChange = useCallback((checked: boolean) => {
        setIsWebview(checked);
        if (!checked) {
            setWebviewUrl('');
            setWebToolType('');
        }
    }, []);

    const handleContentLabelsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setContentLabelsInput(value);

        // Parse and update content labels array
        const labels = value.split(/[\s,，,]+/).map(s => s.trim()).filter(Boolean);
        setContentLabels(labels);
    };

    const handleSave = async () => {
        if (!bannerName || (!redirectId.trim() && redirectType !== 'OTHER')) {
            notification.push({
                message: 'Please fill all required fields',
                type: 'error',
            });
            return;
        }

        // Validate scheduler dates
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            if (end <= start) {
                notification.push({
                    message: 'End date must be after start date',
                    type: 'error',
                });
                return;
            }
        }

        // Validate URL for 'OTHER' redirect type
        if (redirectType === 'OTHER' && redirectId) {
            if (!isValidUrl(redirectId)) {
                notification.push({
                    message: 'Invalid URL format',
                    type: 'error',
                });
                return;
            }
        }

        if (isWebview) {
            if (!webviewUrl.trim()) {
                notification.push({
                    message: 'Please enter web view URL',
                    type: 'error',
                });
                return;
            }

            if (!isValidUrl(webviewUrl)) {
                notification.push({
                    message: 'Invalid web view URL format',
                    type: 'error',
                });
                return;
            }
        }

        let uploadedCreativeUrl = '';

        // Upload creative file if it's a new file
        if (creativeFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', creativeFile);
                formData.append('customPath', 'widgets/marketing-banners');

                const res = await uploadMutation.mutateAsync({
                    data: formData,
                });
                uploadedCreativeUrl = res?.data?.data?.key || '';
            } catch (error) {
                notification.push({
                    message: 'Failed to upload creative image',
                    type: 'error',
                });
                return;
            }
        } else if (widgetData?.thumbnail && typeof widgetData.thumbnail === 'string') {
            // Use existing creative if no new file was uploaded
            uploadedCreativeUrl = widgetData.thumbnail;
        }

        const eligibilityRules: EligibilityRules = {};
        if (userStates.length > 0) eligibilityRules.userState = userStates;
        if (contentLabels.length > 0 || freeEpisodes > 0) {
            eligibilityRules.userBehavior = {};
            if (contentLabels.length > 0) eligibilityRules.userBehavior.hasWatchedContentWithLabels = contentLabels;
            if (freeEpisodes > 0) eligibilityRules.userBehavior.hasCompletedFreeEpisodes = freeEpisodes;
        }

        const completionConditions: CompletionConditions = {};
        completionRules.forEach(rule => {
            if (rule === 'fixed_date') completionConditions.fixedDateTime = fixedDateTime;
            if (rule === 'conversion_completion') completionConditions.conversionCompletion = true;
            if (rule === 'manual_disable') completionConditions.manualDisable = true;
        });

        let parentFields = { ...EMPTY_PARENT_PAYLOAD };
        if (shouldResolveLinkedParents(redirectType, redirectId)) {
            try {
                const response = await itemsParentMutation.mutateAsync({
                    parentId: redirectId.trim(),
                    type: toApiEntityType(redirectType),
                });
                parentFields = buildRedirectionParentFields(
                    redirectType,
                    redirectId,
                    pickParentPayloadFields(normalizeLinkedParentsResponse(response))
                );
            } catch {
                notification.push({
                    title: 'Error',
                    message: 'Failed to resolve linked parent data or redirection type and ID mismatch, Please try again.',
                    type: 'error',
                });
                return;
            }
        }

        const data: any = {
            bannerName,
            enabled,
            redirection_to: toApiEntityType(redirectType),
            redirection_id: redirectId.trim(),
            ...parentFields,
            bannerType,
            is_webview: isWebview,
            web_tool_type: isWebview ? webToolType.trim() : '',
            webview_url: isWebview ? webviewUrl.trim() : '',
            widget_code: widget.code,
            widget_type: Number(widget.value),
            thumbnail: uploadedCreativeUrl || '',
            scheduler: (startDate || endDate) ? { startDate, endDate } : null,
            eligibilityRules: Object.keys(eligibilityRules).length > 0 ? eligibilityRules : null,
            completionConditions: Object.keys(completionConditions).length > 0 ? completionConditions : null,
            view_all: false,
            source_id: '',
            source_type: 'MANUAL',
            widget_icon: '',
            content_rule: '',
            content_type: 'STATIC',
        };

        if (bannerType === 'pill') {
            data.priority = priority;
            data.dismissible = dismissible;
        }

        const finalData: any = {
            name: bannerName,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            data
        };

        onSave(finalData);

        // ✅ SUCCESS NOTIFICATION
        notification.push({
            title: 'Success',
            message: 'Marketing Banner widget saved successfully',
            type: 'success',
        })
    };

    const handleCancel = () => {
        onSave(initialData || null);
    };

    return (
        <div className="space-y-6">
            {/* Banner Type Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <div className='flex flex-col gap-1'>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Banner Type</label>
                    <select
                        value={bannerType}
                        onChange={(e) => setBannerType(e.target.value as 'wide' | 'pill')}
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                    >
                        <option value="wide">Wide Banner</option>
                        {/* <option value="pill">Pill Banner</option> */}
                    </select>
                </div>
            </div>

            {/* Basic Configuration */}
            <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Configuration</h3>

                <div className="space-y-3 max-w-3xl rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Banner Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Banner Name *</label>
                            <input
                                value={bannerName}
                                onChange={(e) => setBannerName(e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                                placeholder="Enter banner name"
                            />
                        </div>

                        <div className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Enabled
                            </label>
                            <ToggleField
                                checked={enabled}
                                onChange={(e) => handleEnabledChange(e.target.checked)}
                            />

                        </div>
                    </div>
                </div>

                <div className="space-y-3 max-w-3xl rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Redirect Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Redirect Type *</label>
                            <SingleSelect
                                value={redirectType}
                                options={redirectOptions}
                                placeholder="Select Redirect Type"
                                onChange={handleRedirectTypeChange}
                            />

                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {redirectType === 'OTHER' ? 'URL *' : 'Redirect ID *'}
                            </label>
                            <input
                                type={redirectType === 'OTHER' ? 'url' : 'text'}
                                value={redirectId}
                                onChange={(e) => setRedirectId(e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                                placeholder={redirectType === 'OTHER' ? 'https://example.com' : 'Enter redirect ID'}
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-3 max-w-3xl rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Web View Configuration</h4>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-end md:gap-4">
                            <div className="flex min-h-[44px] items-center gap-3 rounded-lg  bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
                                <input
                                    type="checkbox"
                                    id="marketing-banner-webview"
                                    checked={isWebview}
                                    onChange={(e) => handleWebviewChange(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="marketing-banner-webview" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Web view
                                </label>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Web Tool Type
                                </label>
                                <input
                                    type="text"
                                    value={webToolType}
                                    onChange={(e) => setWebToolType(e.target.value)}
                                    disabled={!isWebview}
                                    className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                                    placeholder="Enter Web Tool Type"
                                />
                            </div>
                        </div>

                        <div className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Web URL
                            </label>
                            <input
                                type="url"
                                value={webviewUrl}
                                onChange={(e) => setWebviewUrl(e.target.value)}
                                disabled={!isWebview}
                                className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                                placeholder="https://example.com"
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-3 max-w-3xl rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Creative</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className='flex flex-col gap-1'>
                            <div className="flex items-center gap-1 mb-3">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Creative Upload
                                </label>
                                <div className="relative group flex items-center">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-400 hover:text-blue-600 cursor-pointer" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                                        <p className="font-semibold mb-1">Image requirements</p>
                                        <ul className="space-y-1">
                                            <li className="flex gap-1">
                                                <span>•</span>
                                                <span>Banner creative image for marketing campaigns</span>
                                            </li>
                                            <li className="flex gap-1">
                                                <span>•</span>
                                                <span>Formats: JPG, PNG, WebP</span>
                                            </li>
                                            <li className="flex gap-1">
                                                <span>•</span>
                                                <span>File size: Max 5MB</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="border-2 border-dashed rounded-lg p-3 hover:border-blue-400 transition">
                                <input
                                    id="thumbnail"
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    className="hidden"
                                    onChange={handleCreativeFileChange}
                                />
                                <label htmlFor="thumbnail" className="flex flex-col items-center justify-center cursor-pointer">
                                    {creativePreview ? (
                                        <img
                                            src={creativePreview}
                                            alt="Creative preview"
                                            className="w-full h-32 object-cover rounded border"
                                        />
                                    ) : (
                                        <>
                                            <Upload
                                                className="text-gray-400 mb-2"
                                                size={25}
                                            />
                                            <p className="text-sm text-gray-500">
                                                Click to upload creative
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                JPG / PNG / WebP · Max 5MB
                                            </p>
                                        </>
                                    )}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scheduler */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Scheduler</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                        <input
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                        <input
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>
                </div>
            </div>

            {/* Eligibility Rules */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Eligibility Rules</h3>

                <div className='flex flex-col gap-1'>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">User State</label>
                    <div className="space-y-2">
                        {userStateOptions.map((option) => (
                            <div key={option.value} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id={`user-state-${option.value}`}
                                    checked={userStates.includes(option.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setUserStates([...userStates, option.value]);
                                        } else {
                                            setUserStates(userStates.filter(state => state !== option.value));
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`user-state-${option.value}`} className="text-sm text-gray-700 dark:text-gray-300">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Content Labels</label>
                        <input
                            value={contentLabelsInput}
                            onChange={handleContentLabelsInputChange}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="drama, comedy, action"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Free Episodes Completed</label>
                        <input
                            type="number"
                            value={freeEpisodes}
                            onChange={(e) => setFreeEpisodes(Number(e.target.value))}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="0"
                        />
                    </div>
                </div>
            </div>

            {/* Completion Conditions */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Completion Conditions</h3>

                <div className='flex flex-col gap-1'>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Completion Rules</label>
                    <div className="space-y-2">
                        {completionRuleOptions.map((option) => (
                            <div key={option.value} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id={`completion-${option.value}`}
                                    checked={completionRules.includes(option.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setCompletionRules([...completionRules, option.value]);
                                        } else {
                                            setCompletionRules(completionRules.filter(rule => rule !== option.value));
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`completion-${option.value}`} className="text-sm text-gray-700 dark:text-gray-300">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {completionRules.includes('fixed_date') && (
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fixed Date/Time</label>
                        <input
                            type="datetime-local"
                            value={fixedDateTime}
                            onChange={(e) => setFixedDateTime(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>
                )}
            </div>

            {/* Pill Banner Specific */}
            {bannerType === 'pill' && (
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Other Configuration</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                        <div className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                            <input
                                type="number"
                                value={priority}
                                onChange={(e) => setPriority(Number(e.target.value))}
                                min="1"
                                max="3"
                                className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                                placeholder="1"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="dismissible"
                                checked={dismissible}
                                onChange={(e) => setDismissible(e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="dismissible" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Dismissible (X button)
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default MarketingBanner;
